/**
 * Cliente da máquina de inferência nativa.
 *
 * Do outro lado desta fronteira está o llama.cpp compilado em C++, dentro do
 * APK, com acesso à memória do processo — sem WebAssembly no caminho. Aqui só
 * mora a conversa com ele.
 *
 * A fronteira é estreita de propósito: sete operações, nenhuma delas sabendo
 * que motor está atrás. É o que permite trocar llama.cpp por outro runtime
 * depois sem a interface descobrir.
 *
 * O que ESTE arquivo resolve, e que a ponte Java não podia resolver sozinha:
 *
 *  - `@JavascriptInterface` é síncrono e devolve string. Geração de texto não
 *    cabe nesse formato: demora dezenas de segundos e o valor sai aos pedaços.
 *    Por isso o Java devolve um id e manda os tokens por `CustomEvent`; aqui
 *    isso vira uma chamada que se espera e um fluxo que se escuta.
 *  - No navegador não existe camada nativa. Em vez de a tela quebrar com
 *    "undefined is not a function", `disponivel()` responde não, e quem chama
 *    tem uma frase para mostrar.
 */

import { papelPeloNome } from "./gguf-nomes.js";

/** O que a ponte Java expõe. Espelha `LlmBridge.java` — mudar um lado sem o
 *  outro dá erro só em tempo de execução, e dentro do app, que é onde menos se
 *  quer descobrir. */
interface PonteNativa {
  estado(): string;
  modelos(): string;
  apagarModelo(nome: string): boolean;
  baixarModelo(url: string, nome: string): string;
  cancelarDownload(): void;
  carregar(nomeOuCaminho: string, nCtx: number, nThreads: number): string;
  descarregar(): void;
  parar(): void;
  gerar(prompt: string, maxTokens: number, temperatura: number): string;
  gerarChat(mensagensJson: string, maxTokens: number, temperatura: number): string;
  carregarEmbeddings(nomeOuCaminho: string): string;
  descarregarEmbeddings(): void;
  embeddingsCarregado(): boolean;
  embedding(texto: string): string;
  /** Ingestão antecipada. `ingestaoConfiavel` só existe nas pontes em que ela
   *  processa o prompt de verdade — ver `ingestaoDisponivel`. */
  ingerir?(mensagensJson: string, chave: string): string;
  ingestaoConfiavel?(): boolean;
  limparEstados?(): number;
  bytesDosEstados?(): number;
}

/** O GGUF de embedding que a busca semântica usa.
 *
 *  É o MESMO modelo que rodava em ONNX dentro da aba — `multilingual-e5-small`,
 *  384 dimensões, prefixos `query:`/`passage:`. Trocar de motor sem trocar de
 *  modelo é o que mantém válidos os vetores já indexados: não há backfill, e o
 *  `EMBEDDING_MODEL_ID` não precisa mudar. */
export const MODELO_EMBEDDING: ModeloSugerido = {
  nome: "Busca semântica (multilingual-e5-small)",
  arquivo: "multilingual-e5-small-q8_0.gguf",
  url: "https://huggingface.co/cstr/multilingual-e5-small-GGUF/resolve/main/multilingual-e5-small-q8_0.gguf",
  mb: 126,
  descricao:
    "O mesmo modelo que a busca já usava, agora em código nativo. Fica carregado junto com o " +
    "modelo de conversa — são 126 MB contra os 800 MB dele.",
};

export interface Mensagem {
  role: "system" | "user" | "assistant";
  content: string;
}

/** Para que serve um modelo que está no aparelho.
 *
 *  Deduzido, e não perguntado ao arquivo, porque não há como perguntar sem
 *  abri-lo — e abrir um GGUF de 800 MB só para descobrir se ele conversa
 *  custaria a lista inteira demorar minutos.
 *
 *  A dedução é conservadora: `.onnx` é visão, nome de modelo de embedding
 *  conhecido é busca, e todo o resto se presume conversa. Errar para
 *  "conversa" é o erro barato — a pessoa tenta, o modelo responde mal, e ela
 *  troca. Errar para o outro lado esconderia um modelo bom da lista. */
export type PapelDoModelo = "conversa" | "busca" | "visao" | "peca";

export function papelDoModelo(m: ModeloLocal): PapelDoModelo {
  if (m.tipo === "onnx" || m.nome.toLowerCase().endsWith(".onnx")) return "visao";
  // A MESMA regra da busca, e não uma cópia. A busca já sabia recusar projetor
  // e rascunho; esta lista não sabia, e o arquivo que lá não tinha botão aqui
  // aparecia com "carregar" — foi assim que um `mmproj` de 717 MB chegou ao
  // seletor de conversa e devolveu "unsupported model architecture: 'clip'".
  if (papelPeloNome(m.nome).papel !== "modelo") return "peca";
  const n = m.nome.toLowerCase();
  if (n.includes("e5") || n.includes("embed") || n.includes("bge") || n.includes("gte")) {
    return "busca";
  }
  return "conversa";
}

/** Por que uma peça não serve, para a lista poder explicar em vez de só recusar. */
export function avisoDaPeca(m: ModeloLocal): string {
  return papelPeloNome(m.nome).aviso;
}

export interface ModeloSugerido {
  nome: string;
  arquivo: string;
  url: string;
  mb: number;
  descricao: string;
}

/** Modelos que sabemos baixar e rodar. Não é catálogo: é uma lista curta e
 *  conferida, até existir o gerenciador de verdade.
 *
 *  Só repositórios sem restrição de acesso. Os pesos oficiais do Google exigem
 *  aceitar a licença no site e um token pessoal — e um botão que baixa 800 MB
 *  para terminar em 401 é pior que botão nenhum. */
export const SUGERIDOS: ModeloSugerido[] = [
  // Primeiro o de embedding: é pequeno, liga a busca semântica e não compete
  // por memória com o de conversa. Quem baixa os dois fica com as duas coisas.
  {
    nome: "Busca semântica (multilingual-e5-small)",
    arquivo: "multilingual-e5-small-q8_0.gguf",
    url: "https://huggingface.co/cstr/multilingual-e5-small-GGUF/resolve/main/multilingual-e5-small-q8_0.gguf",
    mb: 126,
    descricao:
      "O mesmo modelo que a busca já usava, agora em código nativo. Convive com o modelo de " +
      "conversa — são 126 MB contra os 800 MB dele.",
  },
  {
    nome: "Gemma 3 1B",
    arquivo: "gemma-3-1b-it-Q4_K_M.gguf",
    url: "https://huggingface.co/ggml-org/gemma-3-1b-it-GGUF/resolve/main/gemma-3-1b-it-Q4_K_M.gguf",
    mb: 769,
    descricao:
      "O mesmo modelo que não coube pelo caminho do navegador — lá os pesos sozinhos passavam do " +
      "que o WebAssembly de 32 bits endereça. Aqui roda em código nativo, sem esse teto.",
  },
];

declare global {
  interface Window {
    wuweiAndroidLlm?: PonteNativa;
  }
}

export interface EstadoNativo {
  disponivel: boolean;
  /** Qual variante do motor carregou (`wuweillm_v8_2_dotprod_i8mm`, …).
   *  Interessa porque as instruções `dotprod`/`i8mm` mudam a velocidade por um
   *  fator grande — comparar números entre aparelhos sem saber a variante é
   *  comparar coisas diferentes. */
  variante: string | null;
  motivo: string | null;
  carregado: boolean;
  /** Nome do GGUF que está na memória agora, ou null.
   *
   *  Vem do lado nativo porque é lá que ele vive. A tela chegou a guardar isto
   *  numa variável própria, e a variável mentia: a página pode recarregar sem o
   *  modelo sair da memória, e então a tela concluía "não há modelo" e mandava
   *  carregar de novo o mesmo arquivo — trinta segundos de espera para chegar
   *  ao estado em que já se estava. */
  modelo?: string | null;
  contexto: number;
  nucleos: number;
  aparelho: string;
  ramTotalMb?: number;
  ramLivreMb?: number;
  discoLivreMb?: number;
  erro?: string;
}

export interface ModeloLocal {
  nome: string;
  caminho: string;
  bytes: number;
  /** Qual motor abre este arquivo. Vem do Java, decidido pela extensão — a tela
   *  não deve adivinhar por nome, e no dia de um terceiro formato o campo já
   *  está lá. */
  tipo?: "gguf" | "onnx";
}

export interface FimDaGeracao {
  texto: string;
  ms: number;
  primeiroTokenMs: number;
  pedacos: number;
  /** Pedaços por segundo, descontado o tempo até o primeiro token — que é
   *  ingestão de prompt, não geração. Somar os dois faz prompt longo parecer
   *  modelo lento. */
  porSegundo: number;
  /** "aproveitado/total" em tokens de prompt, direto do motor.
   *
   *  É o que separa "modelo lento" de "releu a conversa inteira". Quando o
   *  aproveitado é zero, nenhum pedaço do cache de atenção foi reusado e a
   *  ingestão inteira foi refeita. Vazio quando a ponte é antiga. */
  prefill: string;
}

function ponte(): PonteNativa | null {
  return typeof window !== "undefined" && window.wuweiAndroidLlm ? window.wuweiAndroidLlm : null;
}

/** Se existe camada nativa nesta origem.
 *
 *  Falso no navegador, e isso não é defeito: é o desenho. O modelo roda dentro
 *  do APK porque é lá que existe processo com memória, thread e SIMD. A tela
 *  precisa dizer isso, não esconder a aba — sumir com o recurso faria parecer
 *  que ele não existe. */
export function disponivel(): boolean {
  return ponte() !== null;
}

export const MOTIVO_SEM_PONTE =
  "A inferência local roda em código nativo, dentro do app instalado. " +
  "Neste navegador não existe essa camada — o modelo não teria onde rodar. " +
  "Abra pelo app do wuwei para usar os agentes locais.";

export function estado(): EstadoNativo | null {
  const p = ponte();
  if (!p) return null;
  try {
    return JSON.parse(p.estado()) as EstadoNativo;
  } catch {
    return null;
  }
}

export function modelos(): ModeloLocal[] {
  const p = ponte();
  if (!p) return [];
  try {
    return JSON.parse(p.modelos()) as ModeloLocal[];
  } catch {
    return [];
  }
}

export function apagarModelo(nome: string): boolean {
  const p = ponte();
  return p ? p.apagarModelo(nome) : false;
}

type Evento = { tipo: string; id?: string; arquivo?: string; [k: string]: unknown };

/** Escuta o canal único que o Java usa para tudo.
 *
 *  Um canal só, com `tipo` dentro, em vez de um evento por assunto: assim quem
 *  precisa acompanhar uma geração registra um ouvinte e o remove no fim, sem
 *  ter de lembrar de quantos tipos existem. */
function ouvir(aoEvento: (e: Evento) => void): () => void {
  const h = (ev: Event) => aoEvento((ev as CustomEvent).detail as Evento);
  window.addEventListener("wuwei-llm", h as EventListener);
  return () => window.removeEventListener("wuwei-llm", h as EventListener);
}

/** Desiste depois de um tempo sem sinal de vida.
 *
 *  Toda espera aqui depende de um evento vindo do Java. Se ele se perder — a
 *  página recarregou no meio, o `evaluateJavascript` não chegou, o processo foi
 *  suspenso — a promessa fica pendurada para sempre, e com ela o botão
 *  desabilitado que só volta ao normal quando ela resolve. Na tela isso é
 *  indistinguível de travamento, e não há como sair sem reiniciar o app.
 *
 *  Aconteceu no primeiro teste automatizado desta tela, que travou 60 segundos
 *  esperando um evento que a ponte falsa não emitia. Um teste que trava por um
 *  defeito de teste é sorte; em produção, o mesmo defeito seria do usuário.
 *
 *  O prazo é reiniciado a cada sinal — geração longa é normal, silêncio longo
 *  não é. */
function comPrazo<T>(
  executar: (
    resolver: (v: T) => void,
    rejeitar: (e: Error) => void,
    adiar: () => void,
  ) => () => void,
  segundos: number,
  oQue: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let relogio = 0;
    let limpar = () => {};
    const encerrar = () => {
      clearTimeout(relogio);
      limpar();
    };
    const adiar = () => {
      clearTimeout(relogio);
      relogio = window.setTimeout(() => {
        limpar();
        reject(new Error(`${oQue}: sem resposta do motor por ${segundos}s`));
      }, segundos * 1000);
    };
    limpar = executar(
      (v) => {
        encerrar();
        resolve(v);
      },
      (e) => {
        encerrar();
        reject(e);
      },
      adiar,
    );
    adiar();
  });
}

/** Carrega um GGUF. Resolve quando o modelo está pronto, rejeita com o motivo.
 *
 *  A carga demora — dezenas de segundos para um modelo de alguns GB — e é
 *  justamente por isso que ela é assíncrona de verdade e não um `await` sobre
 *  uma chamada síncrona: a interface precisa continuar respondendo enquanto o
 *  arquivo é lido do disco. */
export function carregar(
  nome: string,
  opcoes: { contexto?: number; threads?: number } = {},
): Promise<{ ms: number; contexto: number }> {
  const p = ponte();
  if (!p) return Promise.reject(new Error(MOTIVO_SEM_PONTE));
  // 5 min: carregar um GGUF de vários GB num aparelho lento leva minutos, e
  // desistir cedo seria pior que esperar.
  return comPrazo<{ ms: number; contexto: number }>(
    (resolver, rejeitar) => {
      const parar = ouvir((e) => {
        if (e.tipo === "carregado") {
          resolver({ ms: Number(e.ms) || 0, contexto: Number(e.contexto) || 0 });
        } else if (e.tipo === "falhou") {
          rejeitar(new Error(String(e.erro || "o modelo não carregou")));
        }
      });
      const recusa = p.carregar(nome, opcoes.contexto ?? 2048, opcoes.threads ?? 4);
      // Recusa imediata (arquivo ausente, motor indisponível) não passa por
      // evento: o Java devolve na hora.
      if (recusa) rejeitar(new Error(recusa));
      return parar;
    },
    300,
    "carregar o modelo",
  );
}

/** Baixa um GGUF. `aoProgresso` recebe o percentual e os MB já vindos.
 *
 *  O download roda no lado nativo e sobrevive a esta promessa: sair da tela no
 *  meio não cancela nada, e voltar reencontra o arquivo pronto. Quem quiser
 *  parar de verdade chama `cancelarDownload`. */
export function baixar(
  m: ModeloSugerido,
  aoProgresso: (pct: number, mb: number, totalMb: number) => void = () => {},
): Promise<void> {
  const p = ponte();
  if (!p) return Promise.reject(new Error(MOTIVO_SEM_PONTE));
  return new Promise((resolve, reject) => {
    const parar = ouvir((e) => {
      if (e.tipo === "baixando" && e.arquivo === m.arquivo) {
        aoProgresso(Number(e.pct) || 0, Number(e.mb) || 0, Number(e.totalMb) || 0);
      } else if (e.tipo === "baixado" && e.arquivo === m.arquivo) {
        parar();
        resolve();
      } else if (e.tipo === "erroDownload" && e.arquivo === m.arquivo) {
        parar();
        reject(new Error(String(e.erro || "o download falhou")));
      }
    });
    const recusa = p.baixarModelo(m.url, m.arquivo);
    if (recusa) {
      parar();
      reject(new Error(recusa));
    }
  });
}

export function cancelarDownload(): void {
  ponte()?.cancelarDownload();
}

/* ── Embeddings ───────────────────────────────────────────────────────────── */

export function embeddingsCarregado(): boolean {
  const p = ponte();
  try {
    return p ? p.embeddingsCarregado() : false;
  } catch {
    return false;
  }
}

/** Carrega o modelo de embedding. Devolve "" ou o motivo. */
export function carregarEmbeddings(arquivo = MODELO_EMBEDDING.arquivo): string {
  const p = ponte();
  if (!p) return MOTIVO_SEM_PONTE;
  return p.carregarEmbeddings(arquivo);
}

export function descarregarEmbeddings(): void {
  ponte()?.descarregarEmbeddings();
}

/** Estado da tentativa de carregar sob demanda.
 *
 *  Sem esta lembrança, cada busca num aparelho sem o modelo baixado tentaria
 *  carregar de novo e falharia de novo — barato uma vez, desperdício a cada
 *  tecla digitada numa busca incremental. */
let tentouCarregarEmb = false;

/** Garante o modelo de embedding carregado, se o arquivo estiver no aparelho.
 *
 *  Sob demanda, e não no arranque do app, porque são 126 MB que só fazem
 *  sentido para quem usa busca semântica. Quem nunca buscar não paga. */
export function garantirEmbeddings(): boolean {
  if (embeddingsCarregado()) return true;
  if (tentouCarregarEmb) return false;
  tentouCarregarEmb = true;
  if (!modelos().some((m) => m.nome === MODELO_EMBEDDING.arquivo)) return false;
  return carregarEmbeddings() === "" && embeddingsCarregado();
}

/** O vetor de um texto, ou null quando o caminho nativo não está pronto.
 *
 *  Devolve `null` em vez de lançar, porque quem chama tem um plano B — o
 *  caminho antigo, em WebAssembly. Exceção obrigaria um `try` em torno de cada
 *  chamada só para descobrir "aqui não tem". */
export function embedding(texto: string): Float32Array | null {
  const p = ponte();
  if (!p) return null;
  try {
    const cru = JSON.parse(p.embedding(texto)) as number[];
    return cru.length ? new Float32Array(cru) : null;
  } catch {
    return null;
  }
}

export function descarregar(): void {
  ponte()?.descarregar();
}

export function parar(): void {
  ponte()?.parar();
}

/** Gera texto, chamando `aoToken` a cada pedaço.
 *
 *  Resolve com os números da rodada — eles são metade do motivo de esta bancada
 *  existir. Sem `porSegundo` e `primeiroTokenMs` não dá para comparar este
 *  caminho com o anterior, e comparar era o ponto. */
export function gerar(
  prompt: string,
  opcoes: { maxTokens?: number; temperatura?: number } = {},
  aoToken: (texto: string) => void = () => {},
): Promise<FimDaGeracao> {
  return disparar(
    (p, o) => p.gerar(prompt, o.maxTokens, o.temperatura),
    opcoes,
    aoToken,
  );
}

/** Conversa: aplica o template do próprio modelo antes de gerar.
 *
 *  É o caminho certo para perguntar alguma coisa. `gerar` manda texto cru, e
 *  um modelo instruído responde a texto cru CONTINUANDO ele — "Olá" vira o
 *  começo de uma redação, sem erro nenhum no meio do caminho. */
export function gerarChat(
  mensagens: Mensagem[],
  opcoes: { maxTokens?: number; temperatura?: number } = {},
  aoToken: (texto: string) => void = () => {},
): Promise<FimDaGeracao> {
  return disparar(
    (p, o) => p.gerarChat(JSON.stringify(mensagens), o.maxTokens, o.temperatura),
    opcoes,
    aoToken,
  );
}

/** O que a ingestão antecipada devolve quando termina. */
export interface FimDaIngestao {
  /** "aproveitado/total" em tokens, igual ao da geração. */
  prefill: string;
  /** Tokens que vieram do arquivo em disco (0 se não havia arquivo). */
  restaurados: number;
  /** Tempo da ingestão em si, sem o de gravar. */
  ingestaoMs: number;
  /** Tempo de gravar o estado em disco, quando gravou. */
  salvarMs: number;
  /** Tamanho do arquivo de estado, em bytes. */
  bytesEmDisco: number;
  ms: number;
}

/** Ingere o histórico agora, sem gerar resposta.
 *
 *  A promessa resolve quando o motor terminou de montar a tabela de atenção.
 *  Quem chama não precisa do resultado para seguir — a tela continua viva e a
 *  pessoa pode digitar durante todo o processo; o ganho aparece depois, na
 *  hora de responder. */
export function ingerir(
  mensagens: Mensagem[],
  chave: string,
  aoComecar: () => void = () => {},
  aoRestaurar: (restaurados: number) => void = () => {},
): Promise<FimDaIngestao> {
  const p = ponte();
  const chamar = p?.ingerir;
  if (!p || typeof chamar !== "function") {
    return Promise.reject(new Error("esta versão do aplicativo não sabe ingerir adiantado"));
  }
  return new Promise<FimDaIngestao>((resolver, rejeitar) => {
    const id = chamar.call(p, JSON.stringify(mensagens), chave);
    const parar = ouvir((e) => {
      if (e.id !== id) return;
      if (e.tipo === "ingestao-inicio") {
        aoComecar();
        return;
      }
      if (e.tipo === "ingestao-restaurado") {
        aoRestaurar(Number(e.restaurados) || 0);
        return;
      }
      if (e.tipo === "ingestao-fim") {
        parar();
        resolver({
          prefill: String(e.prefill ?? ""),
          restaurados: Number(e.restaurados) || 0,
          ingestaoMs: Number(e.ingestaoMs) || 0,
          salvarMs: Number(e.salvarMs) || 0,
          bytesEmDisco: Number(e.bytesEmDisco) || 0,
          ms: Number(e.ms) || 0,
        });
      } else if (e.tipo === "ingestao-erro") {
        parar();
        rejeitar(new Error(String(e.erro || "a ingestão falhou")));
      }
    });
  });
}

/** A ponte deste aparelho sabe ingerir de verdade?
 *
 *  A tela vem do servidor e o aplicativo vem do aparelho, e os dois andam em
 *  ritmos diferentes: publicar a tela nova não troca o APK de ninguém. A
 *  primeira versão da ingestão antecipada chamava a função que ANOTA o prompt
 *  em vez da que o processa — o modelo respondia sem ter lido a conversa, com
 *  texto fluente e desconectado. Um aparelho parado naquela versão receberia a
 *  tela nova e repetiria o defeito.
 *
 *  Por isso a pergunta é feita à ponte, e não à tela: a ausência do método
 *  desliga o recurso. Falso é o padrão, e ligar exige prova. */
export function ingestaoDisponivel(): boolean {
  const p = ponte();
  if (!p || typeof p.ingerir !== "function") return false;
  return typeof p.ingestaoConfiavel === "function" && p.ingestaoConfiavel() === true;
}

/** Quanto os estados gravados ocupam no aparelho, em bytes. */
/** Quanto da leitura da conversa já foi feito: "feitos/total".
 *
 *  Vazio quando não há leitura em curso, ou quando o aplicativo instalado é
 *  antigo demais para saber responder — nesse caso a tela simplesmente não
 *  mostra número, que é melhor do que mostrar zero parado. */
export function progressoDaIngestao(): string {
  const p = ponte() as unknown as { progressoDaIngestao?: () => string } | null;
  if (!p || typeof p.progressoDaIngestao !== "function") return "";
  try {
    return p.progressoDaIngestao() || "";
  } catch {
    return "";
  }
}

export function bytesDosEstados(): number {
  const p = ponte();
  return p && typeof p.bytesDosEstados === "function" ? Number(p.bytesDosEstados()) || 0 : 0;
}

/** Apaga os estados gravados. Devolve os bytes liberados. */
export function limparEstados(): number {
  const p = ponte();
  return p && typeof p.limparEstados === "function" ? Number(p.limparEstados()) || 0 : 0;
}

function disparar(
  chamar: (p: PonteNativa, o: { maxTokens: number; temperatura: number }) => string,
  opcoes: { maxTokens?: number; temperatura?: number },
  aoToken: (texto: string) => void,
): Promise<FimDaGeracao> {
  const p = ponte();
  if (!p) return Promise.reject(new Error(MOTIVO_SEM_PONTE));
  // 3 min SEM SINAL, não 3 min no total: cada token reinicia a contagem. Uma
  // resposta longa num aparelho lento é normal; silêncio de três minutos com a
  // geração supostamente em curso não é.
  return comPrazo<FimDaGeracao>(
    (resolver, rejeitar, adiar) => {
      let meuId = "";
      const parar = ouvir((e) => {
        // Filtra por id: duas gerações não rodam ao mesmo tempo (a fila do Java
        // tem uma thread só), mas uma geração antiga pode ter deixado eventos a
        // caminho, e misturá-los faria o texto sair embaralhado.
        if (meuId && e.id && e.id !== meuId) return;
        if (e.tipo === "token") {
          adiar();
          aoToken(String(e.texto ?? ""));
        } else if (e.tipo === "inicio") {
          adiar();
        } else if (e.tipo === "fim") {
          resolver({
            texto: String(e.texto ?? ""),
            ms: Number(e.ms) || 0,
            primeiroTokenMs: Number(e.primeiroTokenMs) || 0,
            pedacos: Number(e.pedacos) || 0,
            porSegundo: Number(e.porSegundo) || 0,
            prefill: String(e.prefill ?? ""),
          });
        } else if (e.tipo === "erro") {
          rejeitar(new Error(String(e.erro || "a geração falhou")));
        }
      });
      meuId = chamar(p, {
        maxTokens: opcoes.maxTokens ?? 256,
        temperatura: opcoes.temperatura ?? 0.7,
      });
      return parar;
    },
    180,
    "gerar",
  );
}

/** Ponte para os testes, no mesmo padrão de `window.__wuweiProvas`. */
declare global {
  interface Window {
    __wuweiLlmNativo: {
      disponivel: typeof disponivel;
      estado: typeof estado;
      modelos: typeof modelos;
      SUGERIDOS: typeof SUGERIDOS;
      MOTIVO_SEM_PONTE: string;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiLlmNativo = { disponivel, estado, modelos, SUGERIDOS, MOTIVO_SEM_PONTE };
}
